import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import {
  getStoredLocale,
  resolveSelectedLocale,
} from '@formosa/shared/constants/locale';
import { useAuth } from '../../auth/AuthContext';
import SettingsSubPageShell from '../../components/settings/SettingsSubPageShell';
import { useImFeature } from '../../im/ImFeatureProvider';
import { useTranslation } from '../../i18n/LocaleContext';

const APP_VERSION = '0.1.0';

function RowChevron() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const { chatEnabled } = useImFeature();
  const { locale, t } = useTranslation();
  const [languages, setLanguages] = useState([]);
  const [selectedLocale, setSelectedLocale] = useState(() => getStoredLocale());

  useEffect(() => {
    let cancelled = false;

    async function loadLanguages() {
      try {
        const list = await memberApi.listLanguages();
        if (cancelled) {
          return;
        }
        const rows = Array.isArray(list) ? list : [];
        setLanguages(rows);
        setSelectedLocale(resolveSelectedLocale(rows, getStoredLocale()));
      } catch {
        if (!cancelled) {
          setLanguages([]);
        }
      }
    }

    loadLanguages();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSelectedLocale(locale || getStoredLocale());
  }, [locale]);

  const currentLanguageLabel = useMemo(() => {
    const matched = languages.find((item) => item.langCode === selectedLocale);
    if (matched) {
      return matched.langName || matched.langNameCh || matched.langCode;
    }
    return selectedLocale || t('common.dash');
  }, [languages, selectedLocale, t]);

  function handleLogout() {
    logout();
    navigate('/', { replace: true });
  }

  return (
    <SettingsSubPageShell title={t('settings.title')}>
      <div className="settings-card">
        {isAuthenticated && chatEnabled ? (
          <Link to="/chat" className="settings-row settings-row--link">
            <span className="settings-row__label">聊天</span>
            <span className="settings-row__value">
              IM
              <RowChevron />
            </span>
          </Link>
        ) : null}
        <Link to="/support" className="settings-row settings-row--link">
          <span className="settings-row__label">客服中心</span>
          <span className="settings-row__value">
            <RowChevron />
          </span>
        </Link>
        <Link to="/settings/language" className="settings-row settings-row--link">
          <span className="settings-row__label">{t('settings.language')}</span>
          <span className="settings-row__value">
            {currentLanguageLabel}
            <RowChevron />
          </span>
        </Link>
        <div className="settings-row">
          <span className="settings-row__label">{t('settings.fiatCurrency')}</span>
          <span className="settings-row__value">USD</span>
        </div>
        <div className="settings-row settings-row--last">
          <span className="settings-row__label">{t('settings.version')}</span>
          <span className="settings-row__value">v{APP_VERSION}</span>
        </div>
      </div>

      {isAuthenticated ? (
        <button type="button" className="settings-logout-btn" onClick={handleLogout}>
          {t('settings.logout')}
        </button>
      ) : (
        <Link
          to="/login"
          state={{ from: { pathname: '/settings' } }}
          className="settings-logout-btn settings-logout-btn--link"
        >
          {t('settings.login')}
        </Link>
      )}
    </SettingsSubPageShell>
  );
}
